import moment from 'moment'
import React, { ReactNode } from 'react'
import { DispatchProp } from 'react-redux'
import { AnyAction } from 'redux'
import { Page, Spinner } from '../../components'
import { appsStyleSelected } from '../../helpers/app'
import { connectRedux } from '../../helpers/connectRedux'
import { images } from '../../images'
import { AuthenticatedRoutable, IPenalty } from '../../models'
import { LiteralsService } from '../../services'
import { fetchPenaltiesAction } from '../../store/actions'
import { IStoreState } from '../../store/states'
import theme from '../../theme'
import { PenaltyCard } from './PenaltyCard'
import StyledSection from './styles'

interface IProps extends DispatchProp<AnyAction> {
  isFetchingPenalties: boolean
  penalties: IPenalty[]
  penaltyEndDate?: string
}

const STEPS = 3

class Penalties extends AuthenticatedRoutable<IProps> {
  public componentDidMount(): void {
    super.componentDidMount()
    this.props.dispatch(fetchPenaltiesAction())
  }

  public render(): ReactNode {
    const { isFetchingPenalties } = this.props

    return (
      <Page className={ `authenticated ${ appsStyleSelected?.style }` }>
        <StyledSection>
          <h2>{ LiteralsService.get('penalites', true) }</h2>
          {
            isFetchingPenalties
              ? <article className="spinner-container"><Spinner /></article>
              : (
                <>
                  { this.progress }
                  { this.message }
                  { this.penaltiesList }
                </>
              )
          }
        </StyledSection>
      </Page>
    )
  }

  private get faults(): number {
    return this.props.penalties?.length ?? 0
  }

  // A penalty end date still in the future means bookings are blocked.
  private get isBlocked(): boolean {
    const { penaltyEndDate } = this.props
    if (!penaltyEndDate) return false

    return moment(penaltyEndDate).isAfter(moment())
  }

  private get color(): string {
    if (this.isBlocked) return theme.colors.red
    if (this.faults === 0) return theme.colors.green
    if (this.faults === 1) return theme.colors.yellow

    return theme.colors.orangeDark
  }

  private get progress(): ReactNode {
    // 0 faults fills nothing, each fault fills a step, and a block fills the track.
    const filled = this.isBlocked ? STEPS : Math.min(this.faults, STEPS)

    return (
      <div className="progress">
        <div className="faces">
          <img src={ this.faults < 1 ? images.faceHappyGreen : images.faceHappy } alt="" />
          <img src={ this.faults === 1 ? images.faceMehLightOrange : images.faceMeh } alt="" />
          <img src={ this.faults > 1 && !this.isBlocked ? images.faceSadOrange : images.faceSad } alt="" />
          <img src={ this.isBlocked ? images.faceDizzyPink : images.faceDizzy } alt="" />
        </div>
        <div className="track">
          {
            Array.from({ length: STEPS }).map((_, index) => (
              <div
                key={ index }
                className="step"
                style={ index < filled ? { backgroundColor: this.color } : undefined }
              />
            ))
          }
        </div>
      </div>
    )
  }

  private get message(): ReactNode {
    const { penaltyEndDate } = this.props

    return (
      <div className="message">
        <span className="title" style={{ color: this.color }}>
          { LiteralsService.get(this.titleKey, true) }
        </span>
        <p className="subtitle">{ this.subtitle }</p>
        {
          this.isBlocked &&
          <p className="end-date">
            { `${ LiteralsService.get('penaltyEndDate', true) }${ moment(penaltyEndDate).format('L') }` }
          </p>
        }
      </div>
    )
  }

  private get titleKey(): string {
    if (this.isBlocked) return 'penaltyThirdTitle'
    if (this.faults === 0) return 'penaltyCeroTitle'
    if (this.faults === 1) return 'penaltyFirstTitle'

    return 'penaltySecondTitle'
  }

  private get subtitleKey(): string {
    if (this.isBlocked) return 'penaltyThirdSubTitle'
    if (this.faults === 0) return 'penaltyCeroSubTitle'
    if (this.faults === 1) return 'penaltyFirstSubTitle'

    return 'penaltySecondSubTitle'
  }

  // With faults the sentence is composed as in the app — "you have N fault(s) of
  // attendance." — since the subtitle literal is only the trailing fragment. Unlike the
  // app, the fragment is also appended for a single fault, where it was being dropped.
  private get subtitle(): string {
    if (this.isBlocked || !this.faults) return LiteralsService.get(this.subtitleKey, true)

    const fault = LiteralsService.get(this.faults === 1 ? 'penaltyFault' : 'penaltyFaults')

    return `${ LiteralsService.get('penaltyHave', true) } ${ this.faults } ${ fault } ${ LiteralsService.get(this.subtitleKey) }`
  }

  private get penaltiesList(): ReactNode {
    const { penalties } = this.props

    if (!penalties?.length) return null

    return (
      <div className="penalties-list">
        { penalties.map((penalty, index) => <PenaltyCard key={ index } penalty={ penalty } />) }
      </div>
    )
  }
}

const mapStateToProps = ({ penalties, userInfo }: IStoreState) => ({
  isFetchingPenalties: penalties.isFetchingPenalties,
  penalties: penalties.penalties,
  penaltyEndDate: userInfo.penaltyEndDate
})

export default connectRedux(mapStateToProps, Penalties as any)
